import { destroyCookie, parseCookies, setCookie } from 'nookies'
import { pushEvent } from './gtm'
import { onUntrackedPage } from 'utilities/untracked-pages'

const IS_PROD = process.env.APP_ENV === 'production'
const IS_STAGING = process.env.APP_ENV === 'staging'

export const TRACKING_CONSENT_COOKIE = 'ko_tracking_consent'
export const CLICK_IDS_COOKIE = 'ko_click_ids'

const YEAR_IN_SECONDS = 365 * 24 * 60 * 60
const CLICK_ID_TTL_SECONDS = 7 * 24 * 60 * 60

const CLICK_ID_PARAMS = [
  'fbclid',
  'gclid',
  'ttclid',
  'msclkid',
  'utm_campaign_id',
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_term',
  'utm_content',
]

declare global {
  interface Window {
    dataLayer?: Array<Record<string, string> | IArguments>
    fbq?: (...args: Array<string | number | string[] | object>) => void
    ttq?: {
      revokeConsent?: () => void
      grantConsent?: () => void
    }
    uetq?: {
      push?: (...args: Array<string | Record<string, string>>) => void
    }
  }
}

// Consent Mode commands must reach the dataLayer as an `arguments` object —
// pushing a plain array is silently ignored by GTM.
function gtag(..._args: Array<string | Record<string, string | number>>) {
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

const GRANTED_CONSENT = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
  personalization_storage: 'granted',
}

export const gpcActive = () =>
  typeof navigator !== 'undefined' &&
  (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true

export const hasTrackingConsent = () =>
  parseCookies()[TRACKING_CONSENT_COOKIE] === 'granted'

// Server-usable: pass parseCookies(ctx) from getServerSideProps to read the
// landing-page click IDs off the request when URL params have dropped away.
export const parseStoredClickIds = (
  cookies: Record<string, string | undefined>
): Record<string, string> => {
  try {
    return JSON.parse(cookies[CLICK_IDS_COOKIE] || '{}')
  } catch {
    return {}
  }
}

export const getStoredClickIds = (): Record<string, string> =>
  parseStoredClickIds(parseCookies())

// Landing pages load no pixels, so click IDs from ad URLs would be lost by the
// time the visitor consents pages later. Persist them first-party on arrival;
// injectPixels replays them (e.g. as _fbc) once consent exists.
// Bound each stored value: a crafted link with huge params would otherwise
// grow a cookie the browser sends on every request (431s until it expires).
const MAX_CLICK_ID_VALUE_LENGTH = 256

export const captureClickIds = (
  query: Record<string, string | string[] | undefined>
) => {
  if (typeof window === 'undefined') return

  const captured = CLICK_ID_PARAMS.reduce<Record<string, string>>(
    (acc, param) => {
      const value = query[param]
      const first = Array.isArray(value) ? value[0] : value
      if (first) acc[param] = first.slice(0, MAX_CLICK_ID_VALUE_LENGTH)
      return acc
    },
    {}
  )
  if (Object.keys(captured).length === 0) return

  setCookie(
    null,
    CLICK_IDS_COOKIE,
    JSON.stringify({
      ...getStoredClickIds(),
      ...captured,
    }),
    {
      path: '/',
      maxAge: CLICK_ID_TTL_SECONDS,
      sameSite: 'lax',
    }
  )
}

let pixelsInjected = false

const injectScript = (id: string, body: string) => {
  if (document.getElementById(id)) return
  const script = document.createElement('script')
  script.id = id
  script.text = body
  document.body.appendChild(script)
}

const FB_PIXEL_ID = IS_PROD ? '200812520632487' : '514786171554910'
const TIKTOK_PIXEL_ID = IS_PROD
  ? 'D3DU9I3C77U2RE92NLN0'
  : 'D49ON9BC77U6M9K6RGF0'

/**
 * Injects the Meta, TikTok and (in production) Microsoft UET pixels, once.
 * Pixels report the page URL, so they never start on an untracked page
 * (utilities/untracked-pages.ts). They are injected with plain DOM appends,
 * never via React render, so route-change reconciliation can't try to remove
 * them (the crash that originally pushed the trackers into _document.js).
 */
export const injectPixels = () => {
  if (typeof window === 'undefined' || pixelsInjected || onUntrackedPage()) {
    return
  }
  pixelsInjected = true

  if (IS_PROD || IS_STAGING) {
    const { fbclid } = getStoredClickIds()
    if (fbclid && !parseCookies()._fbc) {
      setCookie(null, '_fbc', `fb.1.${Date.now()}.${fbclid}`, {
        path: '/',
        maxAge: CLICK_ID_TTL_SECONDS,
        sameSite: 'lax',
      })
    }
    injectScript(
      'ko-fb-pixel',
      `!function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('dataProcessingOptions', ['LDU'], 0, 0);
      fbq('init', ${FB_PIXEL_ID});
      fbq('track', 'PageView');`
    )

    injectScript(
      'ko-tiktok-pixel',
      `!function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
      var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
      ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
      ttq.load('${TIKTOK_PIXEL_ID}');
      ttq.page();
      }(window, document, 'ttq');`
    )
  }

  if (IS_PROD) {
    injectScript(
      'ko-ms-uet',
      `(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"17431571"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");`
    )
  }
}

const sha256Hex = async (value: string) => {
  const bytes = new TextEncoder().encode(value)
  const digest = await window.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

// GTM-side tags (enhanced conversions and the like) get hashed identifiers
// only — plaintext email/phone must never enter the dataLayer.
export const pushHashedUserInfo = async ({
  email,
  phoneNumber,
}: {
  email?: string
  phoneNumber?: string
}) => {
  if (typeof window === 'undefined' || !window.crypto?.subtle) return
  if (gpcActive() || !hasTrackingConsent()) return

  const payload: Record<string, string> = { event: 'user_info_captured' }
  if (email) payload.sha256_email = await sha256Hex(email.trim().toLowerCase())
  if (phoneNumber) {
    payload.sha256_phone = await sha256Hex(phoneNumber.replace(/\D/g, ''))
  }
  if (payload.sha256_email || payload.sha256_phone) pushEvent(payload)
}

// Returns whether consent was actually granted. Callers must gate the
// server-side consent record on this — under GPC nothing may be granted,
// recorded, or sent, client- or server-side.
export const grantTrackingConsent = (): boolean => {
  if (typeof window === 'undefined' || gpcActive()) return false

  // Idempotent for repeat consent clicks: the cookie write and the GTM
  // trigger event fire once per grant, so tags triggered on
  // tracking_consent_granted can't double-fire. Consent Mode and pixel
  // injection still (re)run — they're safe to repeat and callers rely on
  // the true return to retry the server-side record.
  if (!hasTrackingConsent()) {
    setCookie(null, TRACKING_CONSENT_COOKIE, 'granted', {
      path: '/',
      maxAge: YEAR_IN_SECONDS,
      sameSite: 'lax',
    })
    pushEvent({ event: 'tracking_consent_granted' })
  }
  gtag('consent', 'update', GRANTED_CONSENT)
  injectPixels()
  // Same-session re-grant after a revoke: the injection guard means the
  // scripts already exist with tracker-level consent revoked — grant it back.
  window.fbq?.('consent', 'grant')
  window.ttq?.grantConsent?.()
  window.uetq?.push?.('consent', 'update', { ad_storage: 'granted' })
  return true
}

const DENIED_CONSENT = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  personalization_storage: 'denied',
}

// If the server-side consent record fails, the browser and DB disagree: the
// cookie suppresses every future disclosure while the server gate keeps
// dropping events, with no path back. Revoking clears the cookie (re-opening
// the next consent moment — grant and record are idempotent) and re-denies
// Consent Mode so GTM-side tags stop immediately. Already-injected pixel
// scripts can't be unloaded mid-session; they go quiet on the next page load,
// when nothing re-injects them.
export const revokeTrackingConsent = () => {
  destroyCookie(null, TRACKING_CONSENT_COOKIE, { path: '/' })
  gtag('consent', 'update', DENIED_CONSENT)
  // Silence the already-initialized trackers through their own consent APIs —
  // the scripts can't be unloaded, but they all support runtime revocation.
  window.fbq?.('consent', 'revoke')
  window.ttq?.revokeConsent?.()
  window.uetq?.push?.('consent', 'update', { ad_storage: 'denied' })
}

/**
 * Run on every page load: a visitor who consented pages (or days) ago gets
 * consent mode re-granted and pixels re-injected without a new consent moment.
 */
export const bootTrackingConsent = () => {
  if (typeof window === 'undefined') return
  if (gpcActive() || !hasTrackingConsent()) return

  gtag('consent', 'update', GRANTED_CONSENT)
  injectPixels()
}
